/**
 * Consolidated Polyfills for Legacy Smart TVs
 * LG WebOS 3.x-5.x and Samsung Tizen 2.x-4.x
 * 
 * This file must be imported FIRST before any other imports
 */

// ============================================
// Core-js polyfills (already in dependencies)
// ============================================
import 'core-js/stable';
import 'regenerator-runtime/runtime';

// ============================================
// React App Polyfills
// ============================================
import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';

// ============================================
// AbortController Polyfill
// ============================================
import 'abortcontroller-polyfill';

// ============================================
// Fetch Polyfill
// ============================================
import 'whatwg-fetch';

// ============================================
// Element.matches Polyfill
// ============================================
if (typeof Element !== 'undefined' && !Element.prototype.matches) {
    Element.prototype.matches =
        Element.prototype.msMatchesSelector ||
        Element.prototype.webkitMatchesSelector ||
        function (selector) {
            const matchedNodes = (this.parentNode || this.document || this.ownerDocument).querySelectorAll(selector);
            return Array.prototype.indexOf.call(matchedNodes, this) !== -1;
        };
}

// ============================================
// Element.closest Polyfill
// ============================================
if (typeof Element !== 'undefined' && !Element.prototype.closest) {
    Element.prototype.closest = function (selector) {
        var el = this;
        while (el) {
            if (el.matches && el.matches(selector)) {
                return el;
            }
            el = el.parentElement || el.parentNode;
        }
        return null;
    };
}

// ============================================
// NodeList.forEach Polyfill
// ============================================
if (typeof NodeList !== 'undefined' && NodeList.prototype && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = Array.prototype.forEach;
}

// ============================================
// Object.assign Polyfill
// ============================================
if (typeof Object.assign !== 'function') {
    Object.assign = function (target) {
        'use strict';
        if (target == null) {
            throw new TypeError('Cannot convert undefined or null to object');
        }
        target = Object(target);
        for (var i = 1; i < arguments.length; i++) {
            var source = arguments[i];
            if (source != null) {
                for (var key in source) {
                    if (Object.prototype.hasOwnProperty.call(source, key)) {
                        target[key] = source[key];
                    }
                }
            }
        }
        return target;
    };
}

// ============================================
// Object.entries Polyfill
// ============================================
if (!Object.entries) {
    Object.entries = function (obj) {
        var ownProps = Object.keys(obj);
        var i = ownProps.length;
        var resArray = new Array(i);
        while (i--) {
            resArray[i] = [ownProps[i], obj[ownProps[i]]];
        }
        return resArray;
    };
}

// ============================================
// Object.values Polyfill
// ============================================
if (!Object.values) {
    Object.values = function (obj) {
        var ownProps = Object.keys(obj);
        var i = ownProps.length;
        var resArray = new Array(i);
        while (i--) {
            resArray[i] = obj[ownProps[i]];
        }
        return resArray;
    };
}

// ============================================
// Object.fromEntries Polyfill
// ============================================
if (!Object.fromEntries) {
    Object.fromEntries = function (entries) {
        if (!entries || !entries[Symbol.iterator]) {
            throw new Error('Object.fromEntries requires a single iterable argument');
        }
        var obj = {};
        for (var entry of entries) {
            if (Object(entry) !== entry) {
                throw new TypeError('iterable for fromEntries should yield objects');
            }
            var key = entry[0];
            var value = entry[1];
            Object.defineProperty(obj, key, {
                configurable: true,
                enumerable: true,
                writable: true,
                value: value,
            });
        }
        return obj;
    };
}

// ============================================
// Array.from Polyfill
// ============================================
if (!Array.from) {
    Array.from = function (arrayLike, mapFn, thisArg) {
        var arr = [];
        var length = arrayLike.length;
        for (var i = 0; i < length; i++) {
            if (mapFn) {
                arr.push(mapFn.call(thisArg, arrayLike[i], i));
            } else {
                arr.push(arrayLike[i]);
            }
        }
        return arr;
    };
}

// ============================================
// Array.prototype.find Polyfill
// ============================================
if (!Array.prototype.find) {
    Array.prototype.find = function (callback, thisArg) {
        if (this == null) {
            throw new TypeError('Array.prototype.find called on null or undefined');
        }
        if (typeof callback !== 'function') {
            throw new TypeError('callback must be a function');
        }
        var list = Object(this);
        var length = list.length >>> 0;
        for (var i = 0; i < length; i++) {
            var element = list[i];
            if (callback.call(thisArg, element, i, list)) {
                return element;
            }
        }
        return undefined;
    };
}

// ============================================
// Array.prototype.findIndex Polyfill
// ============================================
if (!Array.prototype.findIndex) {
    Array.prototype.findIndex = function (callback, thisArg) {
        if (this == null) {
            throw new TypeError('Array.prototype.findIndex called on null or undefined');
        }
        if (typeof callback !== 'function') {
            throw new TypeError('callback must be a function');
        }
        var list = Object(this);
        var length = list.length >>> 0;
        for (var i = 0; i < length; i++) {
            if (callback.call(thisArg, list[i], i, list)) {
                return i;
            }
        }
        return -1;
    };
}

// ============================================
// Array.prototype.includes Polyfill
// ============================================
if (!Array.prototype.includes) {
    Array.prototype.includes = function (searchElement, fromIndex) {
        if (this == null) {
            throw new TypeError('Array.prototype.includes called on null or undefined');
        }
        var O = Object(this);
        var len = parseInt(O.length, 10) || 0;
        if (len === 0) {
            return false;
        }
        var n = parseInt(fromIndex, 10) || 0;
        var k;
        if (n >= 0) {
            k = n;
        } else {
            k = len + n;
            if (k < 0) { k = 0; }
        }
        while (k < len) {
            var currentElement = O[k];
            if (searchElement === currentElement ||
                (searchElement !== searchElement && currentElement !== currentElement)) {
                return true;
            }
            k++;
        }
        return false;
    };
}

// ============================================
// Array.prototype.flat Polyfill
// ============================================
if (!Array.prototype.flat) {
    Array.prototype.flat = function (depth) {
        var flattened = [];
        var depthNum = depth === undefined ? 1 : Number(depth);

        function flatten(arr, currentDepth) {
            for (var i = 0; i < arr.length; i++) {
                if (Array.isArray(arr[i]) && currentDepth < depthNum) {
                    flatten(arr[i], currentDepth + 1);
                } else {
                    flattened.push(arr[i]);
                }
            }
        }

        flatten(this, 0);
        return flattened;
    };
}

// ============================================
// Array.prototype.flatMap Polyfill
// ============================================
if (!Array.prototype.flatMap) {
    Array.prototype.flatMap = function (callback, thisArg) {
        return this.map(callback, thisArg).flat();
    };
}

// ============================================
// String.prototype.includes Polyfill
// ============================================
if (!String.prototype.includes) {
    String.prototype.includes = function (search, start) {
        if (typeof start !== 'number') {
            start = 0;
        }
        if (start + search.length > this.length) {
            return false;
        }
        return this.indexOf(search, start) !== -1;
    };
}

// ============================================
// String.prototype.startsWith Polyfill
// ============================================
if (!String.prototype.startsWith) {
    String.prototype.startsWith = function (searchString, position) {
        position = position || 0;
        return this.substr(position, searchString.length) === searchString;
    };
}

// ============================================
// String.prototype.endsWith Polyfill
// ============================================
if (!String.prototype.endsWith) {
    String.prototype.endsWith = function (searchString, length) {
        if (length === undefined || length > this.length) {
            length = this.length;
        }
        return this.substring(length - searchString.length, length) === searchString;
    };
}

// ============================================
// String.prototype.padStart Polyfill
// ============================================
if (!String.prototype.padStart) {
    String.prototype.padStart = function (targetLength, padString) {
        targetLength = targetLength >> 0;
        padString = String(padString || ' ');
        if (this.length >= targetLength) {
            return String(this);
        }
        targetLength = targetLength - this.length;
        if (targetLength > padString.length) {
            padString += padString.repeat(targetLength / padString.length);
        }
        return padString.slice(0, targetLength) + String(this);
    };
}

// ============================================
// String.prototype.padEnd Polyfill
// ============================================
if (!String.prototype.padEnd) {
    String.prototype.padEnd = function (targetLength, padString) {
        targetLength = targetLength >> 0;
        padString = String(padString || ' ');
        if (this.length >= targetLength) {
            return String(this);
        }
        targetLength = targetLength - this.length;
        if (targetLength > padString.length) {
            padString += padString.repeat(targetLength / padString.length);
        }
        return String(this) + padString.slice(0, targetLength);
    };
}

// ============================================
// String.prototype.repeat Polyfill
// ============================================
if (!String.prototype.repeat) {
    String.prototype.repeat = function (count) {
        if (this == null) {
            throw new TypeError("can't convert " + this + " to object");
        }
        var str = '' + this;
        count = +count;
        if (count !== count) {
            count = 0;
        }
        if (count < 0 || count === Infinity) {
            throw new RangeError('Invalid count value');
        }
        count = Math.floor(count);
        if (str.length === 0 || count === 0) {
            return '';
        }
        var result = '';
        while (count > 0) {
            if (count & 1) {
                result += str;
            }
            count >>= 1;
            if (count) {
                str += str;
            }
        }
        return result;
    };
}

// ============================================
// Number.isInteger Polyfill
// ============================================
if (!Number.isInteger) {
    Number.isInteger = function (value) {
        return typeof value === 'number' &&
            isFinite(value) &&
            Math.floor(value) === value;
    };
}

// ============================================
// Number.isFinite Polyfill
// ============================================
if (!Number.isFinite) {
    Number.isFinite = function (value) {
        return typeof value === 'number' && isFinite(value);
    };
}

// ============================================
// Number.isNaN Polyfill
// ============================================
if (!Number.isNaN) {
    Number.isNaN = function (value) {
        return typeof value === 'number' && isNaN(value);
    };
}

// ============================================
// Promise.allSettled Polyfill
// ============================================
if (!Promise.allSettled) {
    Promise.allSettled = function (promises) {
        return Promise.all(
            promises.map(function (promise) {
                return Promise.resolve(promise).then(
                    function (value) {
                        return { status: 'fulfilled', value: value };
                    },
                    function (reason) {
                        return { status: 'rejected', reason: reason };
                    }
                );
            })
        );
    };
}

// ============================================
// Promise.finally Polyfill
// ============================================
if (!Promise.prototype.finally) {
    Promise.prototype.finally = function (callback) {
        var promise = this;
        var constructor = promise.constructor;
        return promise.then(
            function (value) {
                return constructor.resolve(callback()).then(function () {
                    return value;
                });
            },
            function (reason) {
                return constructor.resolve(callback()).then(function () {
                    throw reason;
                });
            }
        );
    };
}

// ============================================
// URL Polyfill (basic)
// ============================================
if (typeof URL === 'undefined' || !URL.prototype || !('searchParams' in URL.prototype)) {
    // URL polyfill is complex - rely on url-polyfill package or core-js
    console.warn('URL/URLSearchParams may not be fully supported');
}

// ============================================
// globalThis Polyfill
// ============================================
/* eslint-disable no-restricted-globals */
if (typeof globalThis === 'undefined') {
    (function () {
        if (typeof window !== 'undefined') {
            window.globalThis = window;
        } else if (typeof self !== 'undefined') {
            self.globalThis = self;
        } else if (typeof global !== 'undefined') {
            global.globalThis = global;
        }
    })();
}
/* eslint-enable no-restricted-globals */

// ============================================
// CustomEvent Polyfill
// ============================================
if (typeof window !== 'undefined' && typeof window.CustomEvent !== 'function') {
    window.CustomEvent = function (event, params) {
        params = params || { bubbles: false, cancelable: false, detail: null };
        var evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
        return evt;
    };
}

// ============================================
// requestAnimationFrame Polyfill
// ============================================
if (typeof window !== 'undefined') {
    window.requestAnimationFrame = window.requestAnimationFrame ||
        window.webkitRequestAnimationFrame ||
        window.mozRequestAnimationFrame ||
        function (callback) {
            return window.setTimeout(callback, 1000 / 60);
        };

    window.cancelAnimationFrame = window.cancelAnimationFrame ||
        window.webkitCancelAnimationFrame ||
        window.mozCancelAnimationFrame ||
        function (id) {
            window.clearTimeout(id);
        };
}

// ============================================
// Performance.now Polyfill
// ============================================
if (typeof window !== 'undefined' && !window.performance) {
    window.performance = {};
}
if (typeof window !== 'undefined' && !window.performance.now) {
    var startTime = Date.now();
    window.performance.now = function () {
        return Date.now() - startTime;
    };
}

// ============================================
// Console fallback (for very old TVs)
// ============================================
if (typeof console === 'undefined') {
    window.console = {
        log: function () { },
        warn: function () { },
        error: function () { },
        info: function () { },
        debug: function () { },
        trace: function () { },
        dir: function () { },
        group: function () { },
        groupEnd: function () { },
        time: function () { },
        timeEnd: function () { },
        assert: function () { },
        clear: function () { }
    };
}

// ============================================
// Set AbortController global
// ============================================
if (typeof window !== 'undefined' && typeof global !== 'undefined') {
    global.AbortController = window.AbortController;
}

// ============================================
// Log polyfill status
// ============================================
console.log('[Polyfills] Legacy Smart TV polyfills loaded');

export { };
